import { InternalTransferMethodEnum, InternalTransferTypeEnum } from '@interfaces';
import { Action, createReducer, on } from '@ngrx/store';
import { AccountTypes, TransactionTypesEnum } from '@shared/models';

import {
  ClearInternalTransferState,
  ClearTransferToSelectedAccount,
  UpdateInternalTransferConvertAvgCostHoldings,
  UpdateInternalTransferConvertSpecIDHoldings,
  UpdateInternalTransferFromAccount,
  UpdateInternalTransferHasAvgCostHoldings,
  UpdateInternalTransferId,
  UpdateInternalTransferPercentageOfHoldings,
  UpdateInternalTransferSelectedAccountTo,
  UpdateInternalTransferSignerState,
  UpdateInternalTransferTotalTransferValue,
  UpdateInternalTransferTransactionType,
  UpdateInternalTransferTransferFrom,
  UpdateInternalTransferTransferMethod,
  UpdateInternalTransferTransferType,
  UpdateInternalTransferTriage,
  UpdateInternalTransferTriageDivorceTransfer,
  UpdateInternalTransferTriageExceptionsApply,
  UpdateInternalTransferTriageIsOwner,
  UpdateInternalTransferTriageTranferConditionsMatch,
  UpdateInternalTransferTriageTransferConditionsMatch,
  UpdateInternalTransferTriageTransferFrom,
  UpdateInternalTransferTriageTransferTo,
} from '../../actions/internal-transfer/internal-transfer.actions';
import * as InternalTransferOpenAccountActions from '../../actions/open-account/internal-transfer-open-account.actions';

export { InternalTransferMethodEnum, InternalTransferTypeEnum } from '@interfaces';
import {
  InternalTransferState as _InternalTransferState,
  InternalTransferTriage as _InternalTransferTriage,
} from '@interfaces/state';

export interface InternalTransferState extends _InternalTransferState {}
export interface InternalTransferTriage extends _InternalTransferTriage {}

export const initialState: InternalTransferState = {
  triage: {
    transferFrom: AccountTypes.EMPTY,
    transferTo: AccountTypes.EMPTY,
    isOwner: null,
    exceptionsApply: false,
    isTransferConditionsAffirmed: false,
    isDivorceTransfer: false,
  },
  transferFrom: {
    selectedAccount: null,
    transferType: InternalTransferTypeEnum.UNKNOWN,
    transferMethod: InternalTransferMethodEnum.SHARES,
    totalTransferValue: '',
    convertSpecIDHoldings: false,
    convertAvgCostHoldings: false,
    hasAvgCostHoldings: false,
    percentageOfHoldings: '',
  },
  transferTo: {
    selectedAccount: null,
  },
  transactionType: TransactionTypesEnum.UNKNOWN,
  transferId: null,
  signerState: null,
};

const configReducer = createReducer(
  initialState,
  on(UpdateInternalTransferTriage, (state, { payload }) => ({
    ...state,
    triage: payload,
  })),
  on(UpdateInternalTransferTriageTransferFrom, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      transferFrom: payload,
    },
  })),
  on(UpdateInternalTransferTriageTransferTo, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      transferTo: payload,
    },
  })),
  on(UpdateInternalTransferTriageIsOwner, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      isOwner: payload,
    },
  })),
  on(UpdateInternalTransferTriageExceptionsApply, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      exceptionsApply: payload,
    },
  })),
  on(UpdateInternalTransferTriageTransferConditionsMatch, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      isTransferConditionsAffirmed: payload,
    },
  })),
  on(UpdateInternalTransferTriageDivorceTransfer, (state, { payload }) => ({
    ...state,
    triage: {
      ...state.triage,
      isDivorceTransfer: payload,
    },
  })),
  on(UpdateInternalTransferTransferFrom, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      ...payload,
    },
  })),
  on(UpdateInternalTransferFromAccount, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      selectedAccount: payload,
    },
  })),
  on(UpdateInternalTransferConvertSpecIDHoldings, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      convertSpecIDHoldings: payload,
    },
  })),
  on(UpdateInternalTransferConvertAvgCostHoldings, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      convertAvgCostHoldings: payload,
    },
  })),
  on(UpdateInternalTransferHasAvgCostHoldings, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      hasAvgCostHoldings: payload,
    },
  })),
  on(UpdateInternalTransferPercentageOfHoldings, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      percentageOfHoldings: payload,
    },
  })),
  on(UpdateInternalTransferSelectedAccountTo, (state, { payload }) => ({
    ...state,
    transferTo: {
      ...state.transferTo,
      ...payload,
    },
  })),
  on(UpdateInternalTransferTransactionType, (state, { payload }) => ({
    ...state,
    transactionType: payload,
  })),
  on(UpdateInternalTransferId, (state, { payload }) => ({
    ...state,
    transferId: payload,
  })),
  on(UpdateInternalTransferSignerState, (state, { payload }) => ({
    ...state,
    signerState: payload,
  })),
  on(UpdateInternalTransferTransferMethod, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      transferMethod: payload,
    },
  })),
  on(UpdateInternalTransferTransferType, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      transferType: payload,
    },
  })),
  on(UpdateInternalTransferTotalTransferValue, (state, { payload }) => ({
    ...state,
    transferFrom: {
      ...state.transferFrom,
      totalTransferValue: payload,
    },
  })),
  on(ClearInternalTransferState, (state) => ({
    ...state,
    triage: {
      ...initialState.triage,
    },
    transferFrom: {
      ...initialState.transferFrom,
    },
    transferTo: {
      ...initialState.transferTo,
    },
    transactionType: initialState.transactionType,
    transferId: initialState.transferId,
    signerState: initialState.signerState,
  })),
  on(ClearTransferToSelectedAccount, (state) => ({
    ...state,
    transferTo: {
      selectedAccount: initialState.transferTo.selectedAccount,
    },
  })),
  on(
    InternalTransferOpenAccountActions.submitInternalTransferOpenAccountResponse,
    (state, prop) => {
      return {
        ...state,
        transferTo: {
          selectedAccount: {
            ...state.transferTo.selectedAccount,
            accountNumber: prop.payload.brokerageAccountNumber,
          },
        },
      };
    },
  ),
);

export function reducer(state: InternalTransferState | undefined, action: Action) {
  return configReducer(state, action);
}

export const getInternalTransferTriage = (state: InternalTransferState) =>
  state ? state.triage : null;

export const getInternalTransferFromAccountType = (state: InternalTransferState) =>
  state ? state.triage.transferFrom : null;

export const getInternalTransferToAccountType = (state: InternalTransferState) =>
  state && state.triage ? state.triage.transferTo : null;

export const getInternalTransferFrom = (state: InternalTransferState) =>
  state ? state.transferFrom : null;

export const getInternalTransferAccountFrom = (state: InternalTransferState) =>
  state && state.transferFrom ? state.transferFrom.selectedAccount : null;

export const getInternalTransferMethod = (state: InternalTransferState) =>
  state ? state.transferFrom.transferMethod : null;

export const getInternalTransferType = (state: InternalTransferState) =>
  state ? state.transferFrom.transferType : null;

export const getInternalTransferIsOwner = (state: InternalTransferState) =>
  state ? state.triage.isOwner : null;

export const getInternalTransferIsTransferConditionMatch = (state: InternalTransferState) =>
  state ? state.triage.isTransferConditionsAffirmed : null;

export const getInternalTransferIsDivorceTransfer = (state: InternalTransferState) =>
  state ? state.triage.isDivorceTransfer : null;

export const getInternalTransferTotalTransferValue = (state: InternalTransferState) =>
  state ? state.transferFrom.totalTransferValue : null;

export const getInternalTransferTo = (state: InternalTransferState) =>
  state ? state.transferTo : null;

export const getInternalTransferAccountTo = (state: InternalTransferState) =>
  state && state.transferFrom ? state.transferTo.selectedAccount : null;

export const getInternalTransferConvertSpecIDHoldings = (state: InternalTransferState) =>
  state ? state.transferFrom.convertSpecIDHoldings : null;

export const getInternalTransferConvertAvgCostHoldings = (state: InternalTransferState) =>
  state ? state.transferFrom.convertAvgCostHoldings : null;

export const getInternalTransferHasAvgCostHoldings = (state: InternalTransferState) =>
  state ? state.transferFrom.hasAvgCostHoldings : null;

export const getInternalTransferTransactionType = (state: InternalTransferState) =>
  state ? state.transactionType : null;

export const getInternalTransferId = (state: InternalTransferState) =>
  state ? state.transferId : null;

export const getInternalTransferSignerState = (state: InternalTransferState) =>
  state ? state.signerState : null;

export const getInternalTransferPercentageOfHoldings = (state: InternalTransferState) =>
  state ? state.transferFrom.percentageOfHoldings : null;
